import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileCheck2, Users, Timer } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { servicesApi } from '../../api/servicesApi';
import { settingsApi } from '../../api/contentApi';
import { Button } from '../../components/ui/Button';
import { ServiceOfferCard } from '../../components/ui/ServiceOfferCard';
import { CategoryFilter } from '../../components/ui/CategoryFilter';
import { IconBadge } from '../../components/ui/IconBadge';
import { Reveal } from '../../components/ui/Reveal';
import { Hero } from '../../components/ui/Hero';
import { TrustBar } from '../../components/ui/TrustBar';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';
import { Seo } from '../../components/ui/Seo';

const AVANTAGES = [
  { icon: Building2, titre: 'Tous types de sites', description: 'Bureaux, commerces, copropriétés, collectivités.' },
  { icon: FileCheck2, titre: 'Cahier des charges', description: 'Contrat sur-mesure, engagements de qualité mesurables.' },
  { icon: Users, titre: 'Équipes dédiées', description: 'Un interlocuteur unique et des intervenants stables.' },
  { icon: Timer, titre: 'Réactivité', description: 'Devis sous 24h ouvrées, intervention rapide.' },
];

export function ProHome() {
  const { data: categories } = useFetch(() => servicesApi.categories('professionnel'), []);
  const { data: services, loading } = useFetch(
    () => servicesApi.list({ univers: 'professionnel', statut: 'publie' }),
    []
  );
  const { data: settings } = useFetch(() => settingsApi.public(), []);
  const [categorieId, setCategorieId] = useState('');

  const heroImage = settings?.['professionnel.hero_image_url'];
  const heroEyebrow = settings?.['professionnel.hero_eyebrow'] || 'Solutions professionnelles';
  const heroTitre = settings?.['professionnel.hero_titre'] || "Des sites entretenus, l'esprit tranquille";
  const heroSousTitre =
    settings?.['professionnel.hero_sous_titre'] ||
    "Entreprises, collectivités, syndics, commerces : nous prenons en charge le nettoyage, l'entretien et la maintenance de vos locaux avec des équipes fiables et suivies.";

  const categoriesParNom = useMemo(() => {
    const map = {};
    categories?.forEach((cat) => {
      map[cat.id] = cat.nom;
    });
    return map;
  }, [categories]);

  const servicesFiltres = categorieId
    ? services?.filter((s) => String(s.category_id) === categorieId)
    : services;

  return (
    <>
      <Seo
        title="Nettoyage & entretien professionnel à Cergy-Pontoise"
        description="Solutions de nettoyage, entretien et maintenance pour entreprises, commerces, copropriétés et collectivités à Cergy, Pontoise et dans toute la Communauté d'Agglomération de Cergy-Pontoise."
        path="/professionnels"
      />

      <Hero
        eyebrow={heroEyebrow}
        title={heroTitre}
        subtitle={heroSousTitre}
        imageUrl={heroImage}
        tone="secondary"
      >
        <Link to="/devis">
          <Button className="btn--pulse">Demander un devis</Button>
        </Link>
      </Hero>

      <TrustBar />

      <section className="section">
        <div className="container">
          <Reveal as="div" className="section__header offers-header">
            <h2>Nos solutions</h2>
            <p className="section__intro">Un accompagnement multiservices adapté à votre activité.</p>

            <CategoryFilter categories={categories} value={categorieId} onChange={setCategorieId} />
          </Reveal>

          {loading && <SkeletonCardGrid />}

          {!loading && servicesFiltres?.length > 0 && (
            <div className="section__grid section__grid--3">
              {servicesFiltres.map((service, i) => (
                <Reveal key={service.id} delay={i * 60}>
                  <ServiceOfferCard
                    service={service}
                    categorieNom={categoriesParNom[service.category_id]}
                    tone="secondary"
                  />
                </Reveal>
              ))}
            </div>
          )}

          {!loading && !servicesFiltres?.length && (
            <p>Les solutions seront bientôt disponibles ici.</p>
          )}
        </div>
      </section>

      <section className="section section--accent">
        <div className="container">
          <Reveal as="div" className="section__header">
            <h2>Pourquoi nous choisir</h2>
          </Reveal>
          <div className="avantages">
            {AVANTAGES.map((a, i) => (
              <Reveal as="div" className="avantage" key={a.titre} delay={i * 70}>
                <IconBadge icon={a.icon} tone="secondary" />
                <h3>{a.titre}</h3>
                <p>{a.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--cta">
        <div className="container">
          <h2>Vos locaux méritent le meilleur entretien</h2>
          <p className="section__intro">Découvrez nos références ou demandez un devis personnalisé.</p>
          <div className="cta-actions">
            <Link to="/devis">
              <Button>Demander un devis</Button>
            </Link>
            <Link to="/blog">
              <Button variant="secondary">Voir nos réalisations</Button>
            </Link>
            <Link to="/particuliers/faq">
              <Button variant="secondary">Consulter la FAQ</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
